/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, Upload, UploadProps } from 'antd';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import type { ToolbarSlot, TransformToolbarSlot } from '@react-pdf-viewer/toolbar';

import { useAppSelector } from 'src/redux/store';
import { setLoading, setOpenAIPDFResponse, setPdfData, setTextQuery, setThreadFocus } from 'src/redux/slices/open_ai';
import { setPdfSocketConnection } from 'src/redux/slices/socket';

import MobileNavigation from 'src/components/organisms/mobile_navigation';
import QueryInput from 'src/components/atoms/query_input';
import WelcomeMessage from 'src/components/molecules/welcome_message_pdf_chat';

import { checkForCredits, getToken, logoutSession, showNoCreditPopUp } from 'src/helpers/rest';
import { getAudioUri } from 'src/helpers/voice';
import { OpenAiResponse } from 'src/interfaces';
import { BASE, PDF_WORKER_URL } from 'src/common/constants';
import { useAudioPlayer } from 'src/hooks/useAudioPlayer';

import closeIcon from 'src/assets/images/close_icon.svg';
import uploadIcon from 'src/assets/images/upload.png';
import LandingIcon from 'src/assets/images/landing_page.gif';
import copyDarkIcon from 'src/assets/images/copy_dark.svg';
import audioIcon from 'src/assets/images/audio.svg';
import audioStopIcon from 'src/assets/images/audio_stop.svg';

import styles from './index.module.scss';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import SECRETS from 'src/common/secrets';

const PDFChat: React.FunctionComponent = () => {
    const { Dragger } = Upload;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        open_ai: { openAIPDFResponse, textQuery, pdfData },
        socket: { pdfSocketConnection }
    } = useAppSelector(state => state);
    const conversationContainerRef = useRef(null);

    const toolbarPluginInstance = toolbarPlugin();
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { jumpToPage } = pageNavigationPluginInstance;
    const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;

    const transform: TransformToolbarSlot = (slot: ToolbarSlot) => ({
        ...slot,
        Open: () => <></>,
        OpenMenuItem: () => <></>,
        SwitchTheme: () => <></>,
        SwitchThemeMenuItem: () => <></>,
    });

    const [width, setWidth] = useState(window.innerWidth);
    const [copied, setCopied] = useState<Array<number>>([]);
    const [copiedIndex, setCopiedIndex] = useState<number>(-1);
    const [autoScroll, setAutoScroll] = useState<boolean>(true);
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);

    const { playAudio, stopAudio, audioIndex, isPlaying, isAPILoading } = useAudioPlayer({
        getAudioUri: getAudioUri
    });
    
    const props: UploadProps = {
        name: 'file',
        multiple: false,
        customRequest: (options: any) => uploadFile(options),
        onChange(info) {
            const { status } = info.file;
            // if (status !== 'uploading') {}
            if (status === 'done') {
                setUploadLoading(false);
            } else if (status === 'error') {
                setUploadLoading(false);
            }
        },
        onDrop(e) {
            console.log('File dropped successfully', e.dataTransfer.files);
        },
        accept: "application/pdf",
        showUploadList: false,
        disabled: uploadLoading
    };

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (copiedIndex !== null) {
            setTimeout(() => {
                setCopied((prevCopied) => {
                    const indexToRemove = prevCopied.indexOf(copiedIndex);
                    if (indexToRemove !== -1) {
                        const temp = [...prevCopied];
                        temp.splice(indexToRemove, 1);
                        return temp;
                    }
                    return prevCopied;
                });
                setCopiedIndex(-1);
            }, 3000);
        }
    }, [copiedIndex]);

    const goToPage = (index: number) => {
        jumpToPage(index);
    };

    const handleCopyClick = (index: number) => {
        // trackEvent(EVENTS.CONVERSATION_SCREEN.TAP_COPY_ICON, {
        //     screen_name: SCREENS.LANDING_PAGE,
        //     utm: true,
        //     search: window.location.search
        // });

        const element = document.getElementById(`textarea-answer-${index}`);
        if (element) {
            const range = document.createRange();
            range.selectNode(element);
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand('copy');
                selection.removeAllRanges();
                setCopied([...copied, index]);
                setCopiedIndex(index);
            }
        }
    };

    const uploadFile = async (options: any) => {
        const { onSuccess, onError, file, onProgress } = options;

        try {
            setUploadLoading(true);
            const credit = await checkForCredits();
            if (!credit || credit <= 0) {
                showNoCreditPopUp(() => {
                    navigate('/my-subscription');
                });
                setUploadLoading(false)
                return;
            };

            const formData = new FormData();
            formData.append('file', file);

            const xhr = new XMLHttpRequest();

            xhr.open('POST', (process.env.REACT_APP_API_BASE ?? SECRETS.REACT_APP_API_BASE) + '/pdf', true);
            xhr.setRequestHeader('accept', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer ' + getToken());

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentCompleted = Math.round((event.loaded * 100) / event.total);
                    onProgress({ percent: percentCompleted });
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.response);
                    onSuccess(response);
                    setUploadLoading(false);
                    dispatch(setTextQuery(''));
                    dispatch(setPdfData(response.data));
                    message.success(`${file.name} file uploaded successfully.`);
                } else {
                    onError(new Error(xhr.statusText));
                    message.error(`${file.name} file upload failed.`);
                }
            };

            xhr.onerror = () => {
                onError(new Error('Network Error'));
                message.error(`${file.name} file upload failed.`);
            };

            xhr.send(formData);
        } catch (error) {
            console.log(error);
            onError(error);
            message.error(`${file.name} file upload failed.`);
        }
    };

    const initSocketConnection = async () => {
        let retryInit = false;
        let retryCount = 0;
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 2000; // 2 seconds delay between retries

        const connectWebSocket = () => {
            try {
                const socket = new WebSocket(BASE.WEBSCOKET_HOST + `/pdfbot/?token=${getToken()}&document_id=${pdfData?.document_id}&document_name=${pdfData?.document_name}`);

                socket.onopen = () => {
                    console.log('WebSocket connection opened');
                    dispatch(setPdfSocketConnection(socket));
                    retryCount = 0;
                };

                socket.onerror = (error) => {
                    console.log('WebSocket error: ', error);
                    retryInit = true;
                    handleConnectionError();
                };

                socket.onclose = (event) => {
                    console.log('WebSocket connection closed', JSON.stringify(event));
                    if ((!event.wasClean || event?.reason === 'keepalive ping timeout') && retryCount < MAX_RETRIES && !retryInit) {
                        handleConnectionError();
                    }
                };

                let data = '';
                let loading_text = '';

                socket.onmessage = (event) => {
                    const parsedEvent = JSON.parse(event.data);
                    if (parsedEvent['message']) {
                        return;
                    }

                    if (parsedEvent?.status_code === 401) {
                        console.log('invalid or expired jwt');
                        logoutSession();
                        return;
                    }

                    if (parsedEvent['stream_status'] === 'started') {
                        data = '';
                        loading_text = '';
                    }

                    if (parsedEvent['text_chunk']) {
                        data = data + parsedEvent['text_chunk'] + ' ';
                        loading_text = '';
                    }

                    dispatch(setOpenAIPDFResponse((prev: OpenAiResponse[]) => {
                        const updatedResponses = [...prev];
                        if (updatedResponses.length > 0) {
                            updatedResponses[updatedResponses.length - 1] = {
                                ...updatedResponses[updatedResponses.length - 1],
                                answer: data.replace(/\n/g, '<br>'),
                                loading_text: loading_text
                            };
                        }
                        return updatedResponses;
                    }));

                    if (parsedEvent['stream_status'] === 'end') {
                        dispatch(setLoading(false));
                        dispatch(setTextQuery(''));
                        dispatch(setOpenAIPDFResponse((prev: OpenAiResponse[]) => {
                            const updatedResponses = [...prev];
                            if (updatedResponses.length > 0) {
                                updatedResponses[updatedResponses.length - 1] = {
                                    ...updatedResponses[updatedResponses.length - 1],
                                    answer: data.replace(/\n/g, '<br>'),
                                    loader: false,
                                    status: parsedEvent['status'] || 'success',
                                    reference_pages: parsedEvent['reference_pages'] || []
                                };
                            }
                            return updatedResponses;
                        }));
                    }
                };

                return socket;
            } catch (error) {
                console.log('Error creating WebSocket connection:', error);
                handleConnectionError();
            }
        };

        const handleConnectionError = () => {
            if (retryCount < MAX_RETRIES) {
                retryCount++;
                console.log(`Retrying connection attempt ${retryCount} of ${MAX_RETRIES}`);
                setTimeout(() => {
                    connectWebSocket();
                }, RETRY_DELAY);
            } else {
                console.log('Max retry attempts reached');
                dispatch(setPdfSocketConnection(null));
                dispatch(setLoading(false));
                message.error("Unable to establish connection. Please try again in a while. If the issue persists, contact support.");
            }
        };

        return connectWebSocket();
    };

    useEffect(() => {
        const initConnection = async () => {
            if (!pdfData?.document_id) return;
            await initSocketConnection();
        };
        initConnection();
    }, [pdfData]);

    const onPressStartStream = () => {
        if (!pdfSocketConnection || !textQuery) return;
        dispatch(setLoading(true));
        pdfSocketConnection.send(JSON.stringify({ question: textQuery }));
        const openAIResponses = [...openAIPDFResponse];
        openAIResponses.push({ query: textQuery, answer: '', loader: true });
        dispatch(setOpenAIPDFResponse([...openAIResponses]));
        dispatch(setTextQuery(''));

        if (!conversationContainerRef.current) return;
        const container = conversationContainerRef.current as HTMLDivElement;
        if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    };

    const handleScroll = () => {
        if (conversationContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = conversationContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop === clientHeight;
            setAutoScroll(isAtBottom);
        }
    };

    useEffect(() => {
        if (!conversationContainerRef.current) return;
        const container = conversationContainerRef.current as HTMLDivElement;
        if (container && autoScroll) {
            container.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
        }
    }, [openAIPDFResponse, conversationContainerRef, autoScroll]);

    return (
        <div className={styles.profile_page}>
            {width < 900 &&
                <div className={styles.mobile_profile_header}>
                    <img className={styles.mobile_logout_icon} src={closeIcon} alt='close_icon' onClick={() => { dispatch(setThreadFocus(true)); navigate('/'); }} />
                    <span className={styles.mobile_profile_heading}>PDF Thread</span>
                </div>
            }
            <div className={styles.conversation_wrapper}>
                <div className={styles.pdf_section_wrapper}>
                    <div className={styles.upload_pdf_wrapper}>
                        {(pdfData?.document_url)
                            ? <Worker workerUrl={PDF_WORKER_URL}>
                                <div className={styles.topbar}>
                                    <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
                                </div>
                                <div className={styles.pdf_view}>
                                    <Viewer
                                        fileUrl={pdfData.document_url}
                                        plugins={[toolbarPluginInstance, pageNavigationPluginInstance]}
                                        enableSmoothScroll={true}
                                    />
                                </div>
                            </Worker>

                            : <Dragger {...props}>
                                {uploadLoading
                                    ? <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                        <img src={LandingIcon} alt='landing_icon' style={{ height: '100px' }} />
                                        <span className={styles.loader_text} >Uploading & Analyzing document...</span>
                                    </div>
                                    : <>
                                        <p className="ant-upload-drag-icon">
                                            <img src={uploadIcon} style={{ height: '50px' }} alt='icon' />
                                        </p>
                                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                        <p className="ant-upload-hint">
                                            {width < 900
                                                ? 'Easily upload your documents here. Simply click or drag your PDF files into the upload area.'
                                                : 'Easily upload your documents here. Simply click or drag your PDF files into the upload area. Our platform will securely process the information, Please note, only PDF files are accepted.'
                                            }
                                        </p>
                                    </>
                                }
                            </Dragger>
                        }
                    </div>
                </div>
                <div className={styles.chat_section_wrapper} style={!pdfData?.document_url ? { alignContent: 'center' } : {}}>
                    <div
                        className={styles.chat_wrapper}
                        style={pdfData?.document_url
                            ? width < 900
                                ? { overflowY: 'auto', overflowX: 'hidden', display: 'flex', justifyContent: 'space-between' }
                                : { height: 'calc(99vh)', overflowY: 'auto', overflowX: 'hidden', justifyContent: 'space-between', marginTop: '2%' }
                            : {}}
                    >
                        <div className={styles.chats_container}>
                            {!pdfData?.document_url
                                ? <WelcomeMessage />
                                : <>
                                    {(!openAIPDFResponse?.length)
                                        ? <>
                                            {width < 900
                                                ? <WelcomeMessage displaySuggestions={true} />
                                                : <WelcomeMessage customStyle={{ height: 'calc(100vh - 185px)', marginTop: '2vh' }} displaySuggestions={true} />
                                            }
                                        </>
                                        : <div className={styles.conversations_wrapper} ref={conversationContainerRef} onScroll={handleScroll}>
                                            {openAIPDFResponse.map((openAIResponse: any, index) => (
                                                <div className={styles.conversation} style={openAIResponse?.status === 'error' ? { border: '1px solid #F10000' } : {}}>
                                                    <span className={styles.user_message} style={openAIResponse?.status === 'error' ? { color: '#F10000' } : {}}>{openAIResponse?.query}</span>

                                                    {openAIResponse?.answer
                                                        && <div className={styles.answer_header}>
                                                            {openAIResponse?.status !== 'error' &&
                                                                <div className={styles.answer_header_left}>
                                                                    <span className={styles.answer_heading}>Answer</span>
                                                                </div>
                                                            }
                                                            {(!openAIResponse.loader && openAIResponse?.status !== 'error') &&
                                                                <div className={styles.icon_wrapper}>
                                                                    {!!(isAPILoading && audioIndex === index) && <>
                                                                        <span className={styles.loader_text} >Generating voice...</span>
                                                                        <img src={LandingIcon} className={styles.loading_icon} alt='loading' />
                                                                    </>}
                                                                    {!!(isPlaying && audioIndex === index) &&
                                                                        <img src={audioStopIcon} className={styles.audio_stop_icon} onClick={stopAudio} alt='stop' />
                                                                    }
                                                                    {((!isPlaying && !isAPILoading) || audioIndex !== index) &&
                                                                        <img src={audioIcon}
                                                                            className={styles.audio_icon}
                                                                            onClick={() => playAudio((openAIResponse?.answer || '').replace(/<br>/g, ' '), index)}
                                                                            alt='play'
                                                                        />
                                                                    }
                                                                    {copied.length && copied.includes(index)
                                                                        ? <span className={styles.copy_text}>Copied!</span>
                                                                        : <img src={copyDarkIcon} className={styles.copy_icon} alt='answer_icon' onClick={() => handleCopyClick(index)} />
                                                                    }
                                                                </div>}
                                                        </div>
                                                    }

                                                    <div id={`textarea-answer-${index}`} className={styles.answer} dangerouslySetInnerHTML={{ __html: openAIResponse?.answer }}></div>

                                                    {openAIResponse.loader &&
                                                        <div className={styles.loader_text_wrapper}>
                                                            <img src={LandingIcon} alt='landing_icon' className={styles.landing_icon} />

                                                            {openAIResponse?.loading_text &&
                                                                <div className={styles.blinking_text}>{openAIResponse?.loading_text}</div>
                                                            }
                                                        </div>
                                                    }

                                                    {!!openAIResponse?.reference_pages && <>
                                                        <span className={styles.references_heading}>References</span>
                                                        <div className={styles.pages_container}>
                                                            {(openAIResponse?.reference_pages || []).map((page: any, _index: number) => (
                                                                <div className={styles.page_box} onClick={() => goToPage(page - 1)}>
                                                                    p. {page}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                </>
                            }
                        </div>
                        {!!(width >= 900 && pdfData?.document_url) &&
                            <div className={styles.query_input_wrapper} style={{ width: '90%', margin: '0px auto 12px auto' }}>
                                <QueryInput onPressStartStream={() => { onPressStartStream() }} disabled={!!!pdfSocketConnection} />
                            </div>
                        }
                    </div>
                </div>
            </div>
            {width < 900 &&
                <div className={styles.query_input_wrapper}>
                    <QueryInput onPressStartStream={() => { onPressStartStream() }} disabled={!!!pdfSocketConnection} />
                </div>
            }
            {width < 900 && <MobileNavigation />}
        </div>
    )
}

export default PDFChat;